// Lo que es propio de la plantilla de SALA del TENS (medicina, cirugía, urgencia,
// recuperación y otros servicios que no son UCI). Plan del mundo TENS, PR5,
// 2026-10-04. Es una HOJA: no importa nada, para que `tens.js` (el compilador de la
// prosa) pueda leer estas etiquetas sin crear un ciclo con `tensServicios.js`.
//
// Cada opción se contrastó contra el TEXTO de las fuentes (workflow del 4-oct con un
// verificador y un escéptico por grupo), y lo que la fuente no dice se declara como
// adaptación de MiPAE en `src/data/bibliografiaTens.js`, jamás como respaldo:
//
//  · Las medidas de prevención de caídas salen del apartado 8 de las orientaciones
//    técnicas de la SSVQ (2017). La fuente NO dice que el técnico aplique cada una:
//    dice que el técnico realiza las actividades que define la enfermera y registra
//    con un ticket la ejecución de las medidas indicadas. Por eso el campo se llama
//    "medidas que ejecuté" y la pantalla lo dice. NO se ofrecen la sujeción física
//    (la fuente la lista sin decir quién decide y en otro apartado desaconseja la
//    contención en pacientes agudos) ni ubicar al paciente en la sala más cercana a la
//    estación (es una decisión del equipo, no del técnico).
//  · De los avisos a la enfermera, solo "signos vitales alterados" tiene respaldo
//    textual (perfiles de urgencia y recuperación de Puerto Montt, y pediatría de Los
//    Andes), y ese aviso se marca en CADA toma de signos, donde nace. El campo "Avisé a la
//    enfermera de" trae solo lo que no tiene un lugar propio donde marcarlo (el aviso de
//    una lesión de piel y el del sitio de inserción también se marcan donde se registran),
//    así el documento no dice dos veces lo mismo ni se contradice. Todo eso es una
//    selección de MiPAE.
//  · "Levantada a sillón" ninguna fuente la nombra. La prosa de movilización y de
//    alimentación no dice "según indicación": nadie registra la indicación y escribirla
//    sería afirmar algo no constatado.
//  · Drenajes: la fuente (perfil de recuperación) dice "colaborar en la medición y
//    eliminación de drenajes, excepto ventriculares y pleurales", así que el límite se ve
//    en la opción. La diuresis es "colaborar en el registro de balance hídrico". Toma de
//    muestra: solo los tres ejemplos que nombra la fuente (orina, deposiciones,
//    expectoración), jamás sangre.
//
// Español de Chile, sin guiones largos ni dos puntos entre frases (norma del fundador).

// ── Medidas de prevención de caídas (apartado 8 de la SSVQ, voz de técnico) ───
// El orden sigue el del protocolo: lo de riesgo bajo, lo que se agrega en mediano y
// lo que se agrega en alto. Las etiquetas son fragmentos en minúscula porque entran a
// la prosa ("se mantienen medidas de prevención: …") y la pantalla las capitaliza.
export const MEDIDAS_CAIDAS_SALA = [
  { id: 'barandas_cama', label: 'barandas de la cama en alto' },
  { id: 'supervisa_sandalias', label: 'uso de sandalias o pantuflas supervisado' },
  { id: 'supervisa_calzado', label: 'uso correcto del calzado y vestuario supervisado' },
  { id: 'supervisa_levantada', label: 'levantada supervisada' },
  { id: 'supervisa_secado', label: 'secado prolijo supervisado' },
  { id: 'asiste_levantarse', label: 'ayuda al levantarse' },
  { id: 'antideslizante', label: 'materiales antideslizantes en zonas de riesgo' },
  { id: 'asiste_ducha', label: 'ducha asistida y supervisada' },
  { id: 'pasamanos_ducha', label: 'uso de pasamanos en la ducha' },
  { id: 'deambulacion_segura', label: 'deambulación en zonas seguras' },
  { id: 'asiste_deambulacion', label: 'deambulación asistida' },
  { id: 'asiste_bano', label: 'levantada al baño asistida' },
];

// Lo demás de "seguridad" que ya existía en UCI y se conserva con el MISMO id y la
// MISMA etiqueta (un catálogo, dos plantillas): no son medidas de caídas.
export const OPCIONES_SEGURIDAD_COMUNES_SALA = [
  { id: 'unidad_ordenada', label: 'unidad ordenada' },
  { id: 'brazalete', label: 'brazalete de identificación verificado' },
];

export const OPCIONES_SEGURIDAD_SALA = [...MEDIDAS_CAIDAS_SALA, ...OPCIONES_SEGURIDAD_COMUNES_SALA];

// ── "Avisé a la enfermera de": el campo nuevo de sala ─────────────────────────
// Solo trae lo que NO tiene un lugar propio donde marcar el aviso: el de los signos
// vitales vive en cada toma, el de una lesión de piel en su pesquisa y el del sitio de
// inserción en su chip. Todo es una selección de MiPAE (ninguna fuente lo lista). Se avisa
// lo que se observa: ninguna opción decide una conducta. Una caída es un evento adverso
// con su propia notificación en cada institución: este aviso no la reemplaza.
export const CAMPO_INFORMA = {
  id: 'informa',
  label: 'Avisé a la enfermera de',
  tipo: 'multi',
  opciones: [
    { id: 'dolor', label: 'dolor referido' },
    { id: 'cambio_conciencia', label: 'cambio en la conciencia o en la conducta' },
    { id: 'intolerancia', label: 'intolerancia a la alimentación' },
    { id: 'caida', label: 'una caída' },
    { id: 'solicitud', label: 'solicitud del paciente o de su familia' },
  ],
};

// ── Opciones que sala agrega a campos que ya existen en UCI ───────────────────
export const OPCIONES_DISPOSITIVOS_EXTRA_SALA = [{ id: 'vvp', label: 'VVP' }];
export const OPCIONES_EDUCACION_EXTRA_SALA = [
  { id: 'prevencion_caidas', label: 'prevención de caídas' },
  { id: 'alimentacion', label: 'alimentación' },
];
export const OPCIONES_OBSERVACION_EXTRA_SALA = [
  { id: 'asiste_traslado', label: 'Asiste a traslado a examen o procedimiento' },
];

// ── Cuidados de la ronda que sala agrega a los de siempre ─────────────────────
// Los verbos de las fuentes son "colaborar" y "según indicación": las etiquetas no
// prometen más. La prosa de cada uno vive en `compilarEvolucionTens`.
export const CUIDADOS_SALA_EXTRA = [
  { id: 'levantada_sillon', label: 'Levantada a sillón' },
  { id: 'deambulacion_asistida', label: 'Deambulación asistida' },
  { id: 'asistencia_alimentacion', label: 'Asistencia en alimentación' },
  { id: 'medicion_diuresis', label: 'Registro de diuresis (balance hídrico)' },
  { id: 'medicion_drenajes', label: 'Medición de drenajes (no ventriculares ni pleurales)' },
  { id: 'toma_muestra', label: 'Toma de muestra (orina, deposiciones o expectoración)' },
];

// ── Motivos de "no realizable" propios de sala ────────────────────────────────
// Las advertencias son de MiPAE (ninguna fuente las dice) y se escribieron tras la
// revisión clínica del 4-oct: no mandan a descargar con almohadas cuando puede
// requerir indicación, no esperan sin avisar y no dejan decidir que una indicación
// terminó. Las etiquetas son frases nominales porque entran a "no realizado por …".
// `titulo: ''` quita el encabezado fijo "Igual puedes proteger." de las rondas: no
// corresponde cuando el paciente no está o duele, y la advertencia habla sola.
export const MOTIVOS_SALA_EXTRA = [
  {
    id: 'indicacion_no_movilizar',
    label: 'Indicación médica de no movilizar',
    titulo: '',
    advertencia: 'Si no se puede girar, pregunta a tu enfermera qué protección corresponde para talones y sacro, y avisa si ves enrojecimiento.',
  },
  {
    id: 'fuera_unidad',
    label: 'Traslado del paciente a examen o procedimiento',
    titulo: '',
    advertencia: 'Cuando vuelva, revisa la piel y retoma el cambio según lo que haya planificado tu enfermera. El tiempo sin moverse también suma presión.',
  },
  {
    id: 'dolor_movilizar',
    label: 'Dolor al movilizar',
    titulo: '',
    advertencia: 'Avisa a tu enfermera ahora. No fuerces el giro y espera su indicación.',
  },
];

// Los campos de sala que NO existen en UCI, o que existen con otras opciones, vistos
// como campos para que el compilador resuelva etiquetas de lo ya registrado aunque
// la plantilla del turno cambie a mitad de turno (nada registrado se esconde).
export const CAMPOS_SALA_RESPALDO = [
  { id: 'seguridad', opciones: OPCIONES_SEGURIDAD_SALA },
  CAMPO_INFORMA,
  { id: 'dispositivos', opciones: OPCIONES_DISPOSITIVOS_EXTRA_SALA },
  { id: 'educacion_tema', opciones: OPCIONES_EDUCACION_EXTRA_SALA },
  { id: 'observacion', opciones: OPCIONES_OBSERVACION_EXTRA_SALA },
];
